<script lang="ts">
  import { cx } from '@emotion/css';
  import throttle from 'lodash/throttle';
  import { onMount, type Snippet } from 'svelte';
  import { ResponsiveVars } from '@twist/core';
  import type { StudioPathLike } from '@sanity/svelte-loader';
  import type { InputValue } from '@portabletext/svelte';
  import { isChartLarge } from '../utils/chart';
  import PortableText from '../../PortableText/PortableText.svelte';
  import Toolbar from './Toolbar.svelte';

  interface Props {
    path: StudioPathLike;
    encodeDataAttribute: (path: StudioPathLike) => string | undefined;
    footnote: InputValue | null;
    headline?: string;
    id?: string;
    legendDisplay?: string;
    renderChart: (element: HTMLElement) => void;
    subhead?: string;
    gridColspan: number;
    legend?: Snippet;
    table: Snippet;
  }

  let {
    path = [],
    encodeDataAttribute,
    footnote,
    headline = '',
    id = '',
    legendDisplay = '',
    renderChart,
    subhead = '',
    gridColspan,
    legend,
    table
  }: Props = $props();

  let chartElement: HTMLElement | undefined = $state(undefined);
  let showTable = $state(false);

  // Because the axes labels are text elements inside of an SVG,
  // the font size in actual appearance will scale along with the chart SVG.
  // We need to change the assigned font size based on the chart width
  // in order to affect how the text elements actually appear.
  let chartViewboxToElementWidthRatio = $state(1);
  let chartWidth = $derived.by(
    () => chartElement?.querySelector('svg')?.viewBox.baseVal.width ?? 1080
  );

  const calculateChartViewboxToElementWidthRatio = throttle(() => {
    if (chartElement) {
      const svg = chartElement?.querySelector('svg');
      const newChartWidth = svg?.scrollWidth ?? chartWidth;
      chartViewboxToElementWidthRatio = chartWidth / newChartWidth;
    }
  }, 600);

  const onViewChange = (newView: boolean) => {
    showTable = newView;
  };

  onMount(() => {
    if (chartElement) {
      renderChart(chartElement);
      calculateChartViewboxToElementWidthRatio();
    }
  });

  $effect(() => {
    if (chartElement) {
      chartElement?.querySelector('svg')?.remove();
      renderChart(chartElement);
    }
  });

  const responsiveVars = new ResponsiveVars()
    .addResponsive('wrapper-padding', { min: '30px', xl: '45px' })
    .addResponsive('headline-font-size', { min: '20px', xl: '36px' })
    .addResponsive('subhead-font-size', { min: '16px', xl: '24px' })
    .addResponsive('chart-row-gap', { min: '24px', xl: '36px' })
    .addResponsive('toolbar-mb', { min: '6px', xl: '9px' })
    .addResponsive('primary-label-font-size', { min: '16px', xl: '24px' }) // used in chart.ts axis labels
    .toCSS();
</script>

<svelte:window on:resize={calculateChartViewboxToElementWidthRatio} />
<div
  class={cx(responsiveVars, 'wrapper', isChartLarge(gridColspan) && 'large-grid')}
  style:--scaling-ratio={chartViewboxToElementWidthRatio}
>
  <div class="above-chart">
    <div class="chart-text">
      {#if headline}
        <p class="headline" data-sanity={encodeDataAttribute([...path, 'headline'])}>{headline}</p>
      {/if}
      {#if subhead}
        <p class="subhead" data-sanity={encodeDataAttribute([...path, 'subhead'])}>{subhead}</p>
      {/if}
    </div>
    <div class="toolbar">
      <Toolbar {onViewChange} {showTable} {gridColspan} />
    </div>
  </div>

  <div class="chart-table-wrapper">
    {#if showTable}
      {@render table()}
    {:else}
      <div
        {id}
        class="chart"
        bind:this={chartElement}
        data-sanity={encodeDataAttribute([...path, '_id'])}
      ></div>
    {/if}
  </div>

  <div class="below-chart">
    {#if legend && legendDisplay !== 'hide' && !showTable}
      <div class="legend" data-sanity={encodeDataAttribute([...path, 'legendDisplay'])}>
        {@render legend()}
      </div>
    {/if}
    {#if footnote}
      <div class="footnotes" data-sanity={encodeDataAttribute([...path, 'footnote'])}>
        <PortableText body={footnote} />
      </div>
    {/if}
  </div>
</div>

<style lang="scss">
  @import 'src/scss/media';

  :global(.chart-table-wrapper .chart svg) {
    width: 150%;

    @include at('lg') {
      width: 100%;
    }
  }

  .wrapper {
    margin: auto;
    position: relative;
    width: 100%;
    max-width: 1200px;
    padding: var(--wrapper-padding);
    border-radius: 24px;
    background-color: var(--twist-color-gray-100);
    color: var(--twist-color-gray-0);
  }

  .toolbar {
    margin-bottom: var(--toolbar-mb);
  }

  .chart-table-wrapper {
    margin-bottom: var(--chart-row-gap);

    .chart {
      line-height: 0;
      overflow-x: scroll;

      &::-webkit-scrollbar {
        height: 10px;
        cursor: pointer;
      }

      &::-webkit-scrollbar-corner {
        background-color: var(--twist-color-gray-100);
      }

      &::-webkit-scrollbar-track {
        background: var(--twist-color-gray-100);
        border-radius: 50px;
        cursor: pointer;
      }

      &::-webkit-scrollbar-thumb {
        background: var(--twist-color-gray-100);
        border: 1px solid var(--twist-color-gray-0);
        border-radius: 12px;
        transition: background-color 1s ease-in-out;
        cursor: pointer;

        :hover {
          background: var(--twist-color-gray-0);
        }
      }
    }
  }

  .above-chart {
    .chart-text {
      margin-bottom: var(--chart-row-gap);

      .headline {
        font-family: var(--twist-font-family-pilat-wide);
        font-size: var(--headline-font-size);
        font-weight: 700;
        line-height: 115%;
      }

      .subhead {
        font-family: var(--twist-font-family-pilat-wide);
        font-size: var(--subhead-font-size);
        font-weight: 500;
        line-height: 125%;
      }
    }
  }

  .below-chart {
    display: flex;
    gap: 32px;
    justify-content: space-between;
    flex-direction: column;

    .legend,
    .footnotes {
      width: 100%;
      flex-grow: 1;
    }

    .legend {
      visibility: visible;
      opacity: 1;
      transition:
        visibility 0.1s ease-in,
        opacity 0.1s ease-in;
    }

    .footnotes {
      --theme-text-color: var(--twist-color-gray-0);
      --theme-portable-text-paragraph-caption-font-size: 14px;
      --theme-portable-text-font: var(--twist-font-family-pp-neue-machina);
      line-height: 140%;
    }
  }

  .large-grid {
    .above-chart {
      .subhead {
        --font-size: 24px;
      }

      .toolbar {
        justify-content: end;
      }
    }

    .below-chart {
      gap: 16px;
      flex-direction: row;

      .legend,
      .footnotes {
        width: 50%;
      }
    }
  }
</style>
